/**
 * SSE Helper Functions
 *
 * Pure utility functions for SSE stream parsing and message ID generation.
 * No React dependencies — safe to use anywhere.
 */

import { parseAGUIEvent } from '../../types/agui';
import type { AGUIEvent } from '../../types/agui';

// Re-export SolomonStreamEvent type for consumers
export type SolomonStreamEventType =
  | 'text'
  | 'thinking'
  | 'tool_use'
  | 'tool_result'
  | 'approval_required'
  | 'done'
  | 'error'
  | 'stream_end';

export type SolomonStreamEvent = {
  type: SolomonStreamEventType;
  content?: string;
  tool?: string;
  toolCallId?: string;
  input?: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
  sessionId?: string;
  error?: string;
  code?: string;
  approvalToken?: string;
  expiresAt?: string;
  description?: string;
};

export function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function parseSSELine(line: string): SolomonStreamEvent | null {
  if (!line.startsWith('data: ')) return null;
  try {
    return JSON.parse(line.slice(6)) as SolomonStreamEvent;
  } catch {
    return null;
  }
}

export function parseSSELineAsAGUI(line: string): AGUIEvent | null {
  if (!line.startsWith('data: ')) return null;
  try {
    return parseAGUIEvent(line.slice(6));
  } catch {
    return null;
  }
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to data URL'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
